import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import abacusService, { TutorAbacusSessionDTO } from '../services/abacusService';

const TutorAbacusSessions: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<TutorAbacusSessionDTO[]>([]);
  const [duration, setDuration] = useState('40');
  const [log, setLog] = useState<TutorAbacusSessionDTO | null>(null);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const data = await abacusService.getTutorSessions();
      setSessions(data);
    } catch {
      setError('Failed to load tutor sessions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSessions();
  }, []);

  const endSession = async (session: TutorAbacusSessionDTO) => {
    const topics = session.plannedTopicIds?.length ? session.plannedTopicIds : session.topics?.map((topic) => topic.topicId) ?? [];
    if (!topics.length) {
      setError('This session has no planned topic.');
      return;
    }
    try {
      const ended = await abacusService.endTutorSession(session.id, {
        durationMinutes: Number(duration),
        topicsCovered: topics,
      });
      setLog(ended);
      await loadSessions();
    } catch {
      setError('Could not end the session.');
    }
  };

  const markMissed = async (sessionId: number) => {
    try {
      await abacusService.markTutorSessionMissed(sessionId);
      await loadSessions();
    } catch {
      setError('Could not mark the session missed.');
    }
  };

  if (loading) {
    return (
      <Box minHeight="60vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={3}>
        Abacus tutor sessions
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {log && (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="session-log">
          {`Ended. Duration ${log.actualDurationMinutes} minutes. Topics ${log.topicsCovered?.join(', ')}. No recording stored.`}
        </Alert>
      )}
      <Stack spacing={2}>
        {sessions.length === 0 && (
          <Typography color="text.secondary">No sessions are assigned to you yet.</Typography>
        )}
        {sessions.map((session) => (
          <Card variant="outlined" key={session.id} data-testid={`tutor-session-${session.id}`}>
            <CardContent>
              <Typography variant="h6">{session.learnerName}</Typography>
              <Typography variant="body2" data-testid="tutor-prep-level">
                {`Level ${session.levelCode ?? 'not set'}`}
              </Typography>
              <Typography variant="body2" data-testid="locked-level-count">
                {`Locked levels: ${session.lockedLevelCount ?? 0}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(session.scheduledAt).toLocaleString()} · {session.status}
              </Typography>
              {session.joinUrl && (
                <Typography variant="body2" data-testid="tutor-session-join" sx={{ mt: 1 }}>
                  {session.joinUrl}
                </Typography>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                {(session.topics ?? []).map((topic) => (
                  <Button
                    key={topic.topicId}
                    component={RouterLink}
                    to={`/tutor/abacus-sessions/${session.id}/topics/${topic.topicId}`}
                    variant="outlined"
                    data-testid={`open-topic-${topic.topicId}`}
                  >
                    {`Open topic ${topic.topicId}`}
                  </Button>
                ))}
              </Stack>
              {session.status === 'SCHEDULED' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                  <TextField
                    label="Duration (minutes)"
                    type="number"
                    value={duration}
                    onChange={(event) => setDuration(event.target.value)}
                    sx={{ maxWidth: 180 }}
                  />
                  <Button variant="contained" onClick={() => void endSession(session)}>
                    End session
                  </Button>
                  <Button variant="text" onClick={() => void markMissed(session.id)}>
                    Mark missed
                  </Button>
                </Stack>
              )}
              {session.status === 'COMPLETED' && (
                <Typography variant="body2" sx={{ mt: 1 }} data-testid="session-log">
                  {`Duration ${session.actualDurationMinutes ?? 0} minutes. Topics ${(session.topicsCovered ?? []).join(', ')}. No recording stored.`}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Container>
  );
};

export default TutorAbacusSessions;
